// reports.repo — ambil tiket in-scope (RLS) untuk Laporan Servis.
import { supabase } from '@core/supabase';
import { rowToTicket, fetchRounds, MONEY_COLS, type TicketRow } from '@core/ticketMap';
import type { Ticket } from '@core/types';

type CustRel = { nama: string; wa: string | null };
interface Raw extends TicketRow {
  rating: number | null; unit: string | null; tindakan: string | null;
  customers: CustRel | CustRel[] | null;
}
export interface RepItem {
  ticket: Ticket; custNama: string; custWa: string | null; unit: string; tindakan: string;
  rating: number | null; tglAmbilISO: string | null; tglISO: string | null;
}

const cOne = (c: Raw['customers']): CustRel | null => (Array.isArray(c) ? c[0] ?? null : c);

/** Nomor tiket yang MASIH punya piutang — disaring di server lewat v_uang_tiket (0089).
 *  Perlu view karena piutang bisa berasal dari sesi LAMA yang belum lunas, jadi tak cukup
 *  melihat kolom `lunas` di baris tiketnya saja. Hasilnya kecil (hanya penunggak). */
export async function nosPiutang(): Promise<string[]> {
  const PAGE = 1000; const out: string[] = [];
  for (let dari = 0; ; dari += PAGE) {
    const { data, error } = await supabase().from('v_uang_tiket').select('no')
      .eq('arsip_keu', false).gt('piutang', 0).order('no').range(dari, dari + PAGE - 1);
    if (error) throw new Error(error.message);
    const hal = (data ?? []) as { no: string }[];
    out.push(...hal.map((r) => r.no));
    if (hal.length < PAGE) break;
  }
  return out;
}

/**
 * Tiket in-scope untuk laporan berbasis daftar.
 *
 * WAJIB berhalaman: PostgREST memotong di 1.000 baris TANPA memberi tanda apa pun. Versi
 * lama sekali tembak, jadi begitu tiket lewat 1.000 (±hari ke-4 pada 300 tiket/hari) semua
 * laporan yang memakainya diam-diam cuma menghitung 1.000 tiket pertama — terbukti kehilangan
 * 98% omzet pada uji 50.000 tiket.
 *
 * `sejak`/`sampai` menyaring di SERVER (kolom tgl_uang, ber-index) — selalu isi kalau
 * layarnya punya periode, supaya yang diunduh cuma sebulan, bukan seumur toko.
 * Untuk angka RINGKASAN jangan pakai ini sama sekali: pakai finance_agg (dijumlah DB).
 */
export interface SaringTiket {
  sejak?: string | null; sampai?: string | null;  // periode (kolom tgl_uang, ber-index)
  dpSaja?: boolean;                                // Laporan DP: hanya tiket ber-DP
  nos?: string[];                                  // batasi ke nomor tertentu (mis. daftar penunggak)
}

export async function fetchAll(f: SaringTiket = {}): Promise<RepItem[]> {
  if (f.nos && !f.nos.length) return [];
  const PAGE = 1000;
  const list: Raw[] = [];
  // `.in()` masuk ke URL → dipecah supaya permintaannya tak kepanjangan
  const kelompok: (string[] | null)[] = f.nos
    ? Array.from({ length: Math.ceil(f.nos.length / 300) }, (_, i) => f.nos!.slice(i * 300, i * 300 + 300))
    : [null];
  for (const grup of kelompok) {
    for (let dari = 0; ; dari += PAGE) {
      let q = supabase()
        .from('tickets').select(`${MONEY_COLS},rating,unit,tindakan,customers(nama,wa)`)
        .eq('arsip_keu', false) // FIN-H1 finScope: kecualikan tiket tutup-buku (arsip_keu) dari omzet/profit laporan
        .order('no').range(dari, dari + PAGE - 1);
      if (grup) q = q.in('no', grup);
      if (f.sejak) q = q.gte('tgl_uang', f.sejak);
      if (f.sampai) q = q.lte('tgl_uang', f.sampai);
      if (f.dpSaja) q = q.gt('dp', 0);
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      const hal = (data ?? []) as unknown as Raw[];
      list.push(...hal);
      if (hal.length < PAGE) break;
    }
  }
  const rounds = await fetchRounds(list.map((r) => r.no));
  return list.map((r) => {
    const c = cOne(r.customers);
    return {
      ticket: rowToTicket(r, rounds.get(r.no) ?? []), custNama: c?.nama ?? '—', custWa: c?.wa ?? null,
      unit: r.unit ?? '—', tindakan: r.tindakan ?? '—',
      rating: r.rating,
      // Saat tiket sedang servis-ulang, `tgl_ambil_iso` DIRESET kosong sehingga tiket hilang dari
      // SEMUA filter periode — uang sesi sebelumnya lenyap dari Laba-Rugi & Arus Kas (bug nyata Apo).
      // Cadangkan ke tanggal ambil sesi TERAKHIR yang punya tanggal.
      // ponytail: seluruh uang tiket jatuh di satu tanggal; kalau nanti butuh per-sesi lintas-bulan,
      // laporan harus dibentang per-sesi (perubahan besar) — sekarang yang penting tak hilang.
      tglAmbilISO: r.tgl_ambil_iso ?? [...(rounds.get(r.no) ?? [])].reverse().find((x) => x.tglAmbilISO)?.tglAmbilISO ?? null,
      tglISO: r.tgl_iso ?? null,
    };
  });
}
